function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* eslint-disable jsx-a11y/alt-text */
import React, { useRef, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import { Card } from '../Card';
import { Loader } from '../Loader';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { debounce } from '../Utils/helper';
import styles from '@dtsl/css-file-upload/dist/index.module.css';
import Error from '@dtsl/icons/dist/icons/react/Error';
import File from '@dtsl/icons/dist/icons/react/File';
import Gif from '@dtsl/icons/dist/icons/react/Gif';
import Image from '@dtsl/icons/dist/icons/react/Image';
import Trash from '@dtsl/icons/dist/icons/react/Trash';
import Video from '@dtsl/icons/dist/icons/react/Video';
const TYPES = {
  ERROR: 'error',
  UPLOADING: 'uploading',
  FILE: 'file',
  VIDEO: 'video',
  GIF: 'gif',
  IMAGE: 'image',
  THUMBNAIL: 'thumbnail'
};
function FileUpload(props) {
  const {
    type = TYPES.IMAGE,
    fileLabel = '',
    className = '',
    dataTestId = '',
    imgMetaData = {},
    fileSubLabel = '',
    deleteButtonProps = null
  } = props;
  const {
    ERROR,
    UPLOADING,
    FILE,
    VIDEO,
    GIF,
    IMAGE,
    THUMBNAIL
  } = TYPES;
  const fileLabelRef = useRef(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const fileUploadClass = classNames({
    [styles['sib-file-upload']]: true,
    [className]: !!className
  });
  const handleDelete = e => {
    e.preventDefault();
    deleteButtonProps?.onClick(e);
  };
  const getType = type => {
    switch (type) {
      case ERROR:
        return /*#__PURE__*/React.createElement(Error, null);
      case VIDEO:
        return /*#__PURE__*/React.createElement(Video, null);
      case UPLOADING:
        return /*#__PURE__*/React.createElement(Loader, {
          isAbsolute: false
        });
      case GIF:
        return /*#__PURE__*/React.createElement(Gif, null);
      case IMAGE:
        return /*#__PURE__*/React.createElement(Image, null);
      case FILE:
        return /*#__PURE__*/React.createElement(File, null);
      case THUMBNAIL:
        return /*#__PURE__*/React.createElement("span", {
          className: styles['sib-file-upload__type--thumbnail-wrapper']
        }, /*#__PURE__*/React.createElement("img", _extends({}, imgMetaData, {
          className: `${styles['sib-file-upload__type--thumbnail']} ${imgMetaData?.className}`
        })));
    }
  };
  const checkOverflow = () => {
    if (fileLabelRef && fileLabelRef.current) {
      const {
        offsetWidth,
        scrollWidth
      } = fileLabelRef.current;
      setIsTooltipVisible(offsetWidth < scrollWidth);
    }
  };
  const {
    execDebounce: handleResize,
    cancelDebounce
  } = debounce(checkOverflow, 300);
  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelDebounce();
    };
  }, [fileLabelRef.current]);
  const fileLabelJSX = /*#__PURE__*/React.createElement(Text, {
    className: styles['sib-file-upload__content--file-label']
  }, fileLabel);
  const fileSubLabelJSX = /*#__PURE__*/React.createElement(Text, {
    size: "sm",
    className: styles['sib-file-upload__content--file-sub-label'],
    color: type === ERROR ? 'var(--sib-color_negative-hover)' : 'var(--sib-color_content-secondary)'
  }, fileSubLabel);
  return /*#__PURE__*/React.createElement(Card, {
    customCard: true,
    className: fileUploadClass,
    dataTestId: dataTestId
  }, /*#__PURE__*/React.createElement(Card.Content, {
    style: {
      padding: 0,
      display: 'flex',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: `${styles['sib-file-upload__type']} ${styles[`sib-file-upload__type--${type}`]}`
  }, getType(type)), /*#__PURE__*/React.createElement("div", {
    className: styles['sib-file-upload__content']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-file-upload__content--description']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-file-upload__content--file-label-wrapper'],
    ref: fileLabelRef
  }, isTooltipVisible ? /*#__PURE__*/React.createElement(Tooltip, {
    text: fileLabel,
    placement: "top-start"
  }, fileLabelJSX) : fileLabelJSX), isTooltipVisible ? /*#__PURE__*/React.createElement(Tooltip, {
    text: fileSubLabel,
    placement: "top-start"
  }, fileSubLabelJSX) : fileSubLabelJSX), deleteButtonProps ? /*#__PURE__*/React.createElement(Button, _extends({
    ariaLabel: "Delete"
  }, deleteButtonProps, {
    onClick: handleDelete,
    size: "small",
    iconAlone: true,
    variant: "destructive-tertiary",
    icon: /*#__PURE__*/React.createElement(Trash, null),
    className: `${styles['sib-file-upload__content--delete-button']} ${deleteButtonProps?.className}`
  })) : null)));
}
FileUpload.propTypes = {
  fileLabel: PropTypes.string.isRequired,
  fileSubLabel: PropTypes.string.isRequired,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.oneOf(Object.values(TYPES)),
  imgMetaData: PropTypes.object,
  deleteButtonProps: PropTypes.object
};
export default FileUpload;